// components/PageWrapper.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DataProvider } from '@plasmicapp/react-web/lib/host';
import { useSupabaseFromContext } from './SupabaseClientContext';
import { useRealtimeSubscription } from './RealtimeProvider';
import { setCookie, deleteCookie, getCookie } from 'cookies-next';
import { isPlasmicProxy } from '../lib/isPlasmicProxy';

const IS_CLIENT = typeof window !== 'undefined';
const DEFAULT_REALTIME_TABLES = ['orders', 'reports', 'parts_queue'];

export function PageWrapper({
  layoutData: layoutDataFromServer,
  children,
  realtimeTriggerTables = DEFAULT_REALTIME_TABLES,
}: {
  layoutData?: any;
  children?: React.ReactNode;
  realtimeTriggerTables?: string[];
}) {
  const supabaseContext = useSupabaseFromContext();
  const [layoutData, setLayoutData] = useState(layoutDataFromServer);
  const [useDevProxy, setUseDevProxy] = useState(false);
  const [proxyReady, setProxyReady] = useState(false);

  // ----------------------------------------------------------------
  // HELPER: Get latest prefs from cookie safely
  // ----------------------------------------------------------------
  const getClientPrefs = useCallback(() => {
    if (!IS_CLIENT) return {};
    const rawPrefs = getCookie('user-prefs');
    if (rawPrefs && typeof rawPrefs === 'string') {
      try {
        return JSON.parse(decodeURIComponent(rawPrefs));
      } catch (e) {
        console.warn("[PageWrapper] Failed to parse user-prefs cookie", e);
      }
    }
    return {};
  }, []);

  // 1. Initialize Proxy & Perform Handshake
  useEffect(() => {
    if (IS_CLIENT) {
      const shouldProxy = isPlasmicProxy();
      setUseDevProxy(shouldProxy);

      if (shouldProxy) {
        console.log("[PageWrapper] Proxy mode detected. Initiating handshake...");
        fetch('/api/proxy-handshake')
          .then(res => res.json())
          .then(data => {
            if (data.fingerprint) {
              // Store fingerprint globally for DataProviders to access
              (window as any).__PROXY_FINGERPRINT__ = data.fingerprint;
              console.log("[PageWrapper] ✅ Proxy fingerprint acquired.");
            } else {
              console.warn("[PageWrapper] ⚠️ Handshake returned no fingerprint:", data);
            }
          })
          .catch(e => console.error("[PageWrapper] ❌ Proxy handshake failed:", e))
          .finally(() => setProxyReady(true));
      } else {
        setProxyReady(true);
      }
    }
  }, []);

  // 2. Hydration & Persistence Logic (FIXED)
  useEffect(() => {
    if (layoutDataFromServer) {
      // Always merge server data with latest client cookies to ensure continuity
      const currentPrefs = getClientPrefs();
      const mergedData = { 
        ...layoutDataFromServer,
        userPrefs: {
            // Prioritize client cookie over server response if both exist
            ...(layoutDataFromServer.userPrefs || {}),
            ...currentPrefs
        }
      };
      setLayoutData(mergedData);
    }
  }, [layoutDataFromServer, getClientPrefs]);

  // 3. Listen for Client-Side User Pref changes
  useEffect(() => {
    if (!IS_CLIENT) return;
    
    const handlePrefChange = (e: CustomEvent) => {
      setLayoutData((prev: any) => ({ 
        ...(prev || {}), 
        userPrefs: { ...(prev?.userPrefs || {}), ...e.detail }
      }));
    };
    
    window.addEventListener('layout-userPrefs-updated', handlePrefChange as EventListener);
    
    // Initial check on mount (in case hydration was stale)
    const prefs = getClientPrefs();
    if (Object.keys(prefs).length > 0 && layoutData) {
      // Only update if actually different to prevent loops
      if (JSON.stringify(prefs) !== JSON.stringify(layoutData.userPrefs)) {
        setLayoutData((prev: any) => ({ ...prev, userPrefs: prefs }));
      }
    }

    return () => window.removeEventListener('layout-userPrefs-updated', handlePrefChange as EventListener);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 4. Metric Refresh Logic (UPDATED)
  const refreshMetrics = useCallback(
    async (reason: string) => {
      if (!proxyReady) return;
      console.log(`[PageWrapper] Refreshing metrics due to: ${reason} (proxy=${useDevProxy})`);

      try {
        let data;
        if (useDevProxy) {
          const fingerprint = (window as any).__PROXY_FINGERPRINT__;
          const resp = await fetch('/api/plasmic-studio-proxy', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'x-proxy-fingerprint': fingerprint || ''
            },
            body: JSON.stringify({
              operation: 'rpc',
              rpc: 'get_current_technician',
              params: {},
            }),
          });
          if (!resp.ok) throw new Error(`Proxy error ${resp.status}`);
          data = await resp.json();
        } else {
          if (!supabaseContext) throw new Error('Supabase client missing');
          const { data: rpcData, error: rpcError } = await supabaseContext.rpc('get_current_technician');
          if (rpcError) throw rpcError;
          data = rpcData;
        }

        const freshData = data?.[0] || null;
        if (freshData) {
          // Merge with current cookie prefs so we don't lose UI state on refresh
          const currentPrefs = getClientPrefs();
          freshData.userPrefs = currentPrefs;
          
          setLayoutData(freshData);
          if (IS_CLIENT) {
            setCookie('technician', JSON.stringify(freshData), { path: '/', sameSite: 'lax' });
          }
        }
      } catch (err) {
        console.error('[PageWrapper] Error refreshing metrics:', err);
      }
    },
    [supabaseContext, useDevProxy, proxyReady, getClientPrefs]
  );

  // 5. Auth Listener
  useEffect(() => {
    if (!supabaseContext || !IS_CLIENT) return;
    const { data: { subscription } } = supabaseContext.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') refreshMetrics(`Auth ${event}`);
      if (event === 'SIGNED_OUT') {
        setLayoutData(null);
        deleteCookie('technician');
      }
    });
    return () => subscription.unsubscribe();
  }, [supabaseContext, refreshMetrics]);

  // 5b. Session Revocation Detector
  // The DB trigger `enforce_single_session` deletes other-device sessions when
  // a user signs in elsewhere. The local Supabase client doesn't know about
  // this until it tries (and fails) to refresh — which can be over an hour.
  // We poll `auth.getUser()` (which validates the JWT server-side against the
  // sessions row) and force a sign-out the moment it comes back invalid.
  useEffect(() => {
    if (!supabaseContext || !IS_CLIENT) return;

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const forceSignOut = async (reason: string) => {
      if (cancelled) return;
      console.warn(`[PageWrapper] Session invalidated (${reason}). Signing out.`);
      try {
        await supabaseContext.auth.signOut();
      } catch (e) {
        console.warn('[PageWrapper] signOut threw, clearing local state anyway', e);
      }
      // Best-effort cleanup so UI flips immediately
      setLayoutData(null);
      deleteCookie('technician');
      // Redirect — full reload to clear any cached SWR data and SSR state.
      if (typeof window !== 'undefined') {
        window.location.href = '/login?reason=session_revoked';
      }
    };

    const validate = async () => {
      // Don't bother if there's no session in the first place.
      const { data: { session }, error: sessionError } = await supabaseContext.auth.getSession();
      if (!session || cancelled || sessionError) return;

      const { data, error } = await supabaseContext.auth.getUser();
      if (cancelled) return;

      if (error) {
        // Prevent accidental sign-outs during heavy query loads / transient network errors
        const status = error.status;
        const isTransient = error.name === 'AuthRetryableFetchError' || 
                            status === 0 || 
                            status === 429 || 
                            (typeof status === 'number' && status >= 500);
        if (isTransient) {
          console.warn('[PageWrapper] Ignored transient error during session validation:', error);
          return;
        }
        await forceSignOut(error.message);
      } else if (!data?.user) {
        await forceSignOut('no user returned');
      }
    };

    // Poll every 30s.
    timer = setInterval(validate, 30_000);

    // Validate the moment the tab regains focus / becomes visible.
    const onFocus = () => validate();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') validate();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Run once on mount.
    validate();

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [supabaseContext]);

  // 6. Initial Load / Recovery
  useEffect(() => {
    if (proxyReady) {
      if (!layoutData && !layoutDataFromServer) {
        supabaseContext?.auth.getSession().then(({ data: { session } }) => {
            if (session) refreshMetrics('recovery');
        });
      }
    }
  }, [proxyReady, refreshMetrics, layoutDataFromServer, layoutData, supabaseContext]);

  // 7. Realtime Setup
  const tablesToSubscribe = useMemo(
    () => Array.from(new Set(realtimeTriggerTables)).filter(Boolean),
    [realtimeTriggerTables]
  );
  useRealtimeSubscription(tablesToSubscribe);

  // 8. Hotkey Debug
  useEffect(() => {
    if (!IS_CLIENT) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.key === 'Enter') {
        e.preventDefault();
        refreshMetrics('manual Alt+Enter debug');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [refreshMetrics]);

  return (
    <DataProvider name="layout" data={layoutData || layoutDataFromServer}>
      {children}
    </DataProvider>
  );
}
